import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { LogOut, Mail, Phone, Inbox } from "lucide-react";
import { Container } from "@/components/site/blocks";
import { useSeo } from "@/components/site/useSeo";
import { api, errorText, getMe } from "@/lib/adminApi";
import { TestimonialsManager } from "@/components/site/TestimonialsManager";

const STATUSES = [
  { v: "new", l: "New" },
  { v: "contacted", l: "Contacted" },
  { v: "in_discussion", l: "In discussion" },
  { v: "closed", l: "Closed" },
];
const fmt = (iso) => new Date(iso).toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" });

const InquiryCard = ({ q, onStatus }) => (
  <li className="rounded-3xl border border-[#D4AF37]/25 bg-[#12141C] p-6 sm:p-8" data-testid={`inquiry-${q.id}`}>
    <div className="flex flex-wrap items-start justify-between gap-4">
      <div>
        <p className="text-xs uppercase tracking-[0.2em] text-[#C8C5BB]">{fmt(q.created_at)}</p>
        <h2 className="mt-2 font-display text-3xl font-semibold">{q.name}</h2>
        <p className="text-[#E6E3DA]">{q.organization}</p>
      </div>
      <label className="text-sm text-[#C8C5BB]">
        <span className="sr-only">Status for {q.name}</span>
        <select value={q.status ?? "new"} onChange={(e) => onStatus(q.id, e.target.value)} data-testid={`inquiry-status-${q.id}`}
          className="rounded-full border border-[#D4AF37]/50 bg-[#0A0B0E] px-4 py-2 text-sm text-[#F9F8F6]">
          {STATUSES.map((s) => <option key={s.v} value={s.v}>{s.l}</option>)}
        </select>
      </label>
    </div>
    <dl className="mt-6 grid gap-4 text-sm sm:grid-cols-2">
      <div><dt className="text-[#C8C5BB]">Audience</dt><dd className="mt-1 text-[#F9F8F6]">{q.audience}</dd></div>
      <div><dt className="text-[#C8C5BB]">Partnership interest</dt><dd className="mt-1 text-[#F3C649]">{q.interest}</dd></div>
    </dl>
    <div className="mt-6 flex flex-wrap gap-5 text-sm">
      <a href={`mailto:${q.email}`} className="inline-flex items-center gap-2 hover:text-[#F3C649]"><Mail size={16} aria-hidden="true" className="text-[#F3C649]" />{q.email}</a>
      {q.phone && <a href={`tel:${q.phone}`} className="inline-flex items-center gap-2 hover:text-[#F3C649]"><Phone size={16} aria-hidden="true" className="text-[#F3C649]" />{q.phone}</a>}
    </div>
  </li>
);

export default function AdminDashboard() {
  useSeo("Inquiry dashboard", "Private admin area.");
  const nav = useNavigate();
  const [items, setItems] = useState(null);
  const [filter, setFilter] = useState("all");
  const [tab, setTab] = useState("inquiries");

  useEffect(() => {
    let alive = true;
    (async () => {
      const me = await getMe();
      if (!alive) return;
      if (!me) { nav("/admin/login", { replace: true }); return; }
      const { data } = await api.get("/admin/inquiries");
      if (alive) setItems(Array.isArray(data) ? data : []);
    })();
    return () => { alive = false; };
  }, [nav]);

  const onStatus = async (id, status) => {
    try {
      await api.patch(`/admin/inquiries/${id}`, { status });
      setItems((xs) => xs.map((x) => (x.id === id ? { ...x, status } : x)));
      toast.success("Status updated");
    } catch (e) {
      toast.error(errorText(e.response?.data?.detail));
    }
  };

  const logout = async () => { await api.post("/auth/logout"); nav("/admin/login", { replace: true }); };
  const list = (items ?? []).filter((x) => filter === "all" || (x.status ?? "new") === filter);
  const count = (v) => (items ?? []).filter((x) => (x.status ?? "new") === v).length;

  return (
    <section className="min-h-[80vh] pb-24 pt-32" data-testid="admin-dashboard">
      <Container>
        <div className="flex flex-wrap items-end justify-between gap-6">
          <div>
            <p className="label">Private</p>
            <h1 className="mt-3 font-display text-5xl font-semibold sm:text-6xl">{tab === "inquiries" ? "Partnership inquiries" : "Testimonials"}</h1>
          </div>
          <button type="button" onClick={logout} className="btn-ghost" data-testid="admin-logout-btn"><LogOut size={16} aria-hidden="true" /> Sign out</button>
        </div>
        <div className="mt-8 inline-flex rounded-full border border-[#D4AF37]/40 p-1" role="group" aria-label="Dashboard section">
          {[{ v: "inquiries", l: "Inquiries" }, { v: "testimonials", l: "Testimonials" }].map((t) => (
            <button key={t.v} type="button" aria-pressed={tab === t.v} onClick={() => setTab(t.v)} data-testid={`admin-tab-${t.v}`}
              className={`rounded-full px-5 py-2 text-sm font-semibold transition-colors ${tab === t.v ? "bg-[#F3C649] text-[#0A0B0E]" : "text-[#F9F8F6] hover:bg-[#F3C649]/10"}`}>{t.l}</button>
          ))}
        </div>
        {tab === "testimonials" ? <TestimonialsManager /> : (<>
        <div className="mt-10 flex flex-wrap gap-2" role="group" aria-label="Filter by status">
          {[{ v: "all", l: "All", n: items?.length ?? 0 }, ...STATUSES.map((s) => ({ ...s, n: count(s.v) }))].map((f) => (
            <button key={f.v} type="button" aria-pressed={filter === f.v} onClick={() => setFilter(f.v)} data-testid={`admin-filter-${f.v}`}
              className={`rounded-full border px-4 py-2 text-sm transition-colors ${filter === f.v ? "border-[#F3C649] bg-[#F3C649] text-[#0A0B0E]" : "border-[#D4AF37]/40 text-[#F9F8F6] hover:bg-[#F3C649]/10"}`}>
              {f.l} <span className="ml-1 opacity-80">{f.n}</span>
            </button>
          ))}
        </div>
        {items === null && <p className="mt-12 text-[#C8C5BB]">Loading…</p>}
        {items !== null && list.length === 0 && (
          <div className="mt-12 flex flex-col items-center rounded-3xl border border-dashed border-[#D4AF37]/40 p-14 text-center" data-testid="admin-empty">
            <Inbox size={30} aria-hidden="true" className="text-[#F3C649]" />
            <p className="mt-4 font-display text-2xl">No inquiries here yet.</p>
          </div>
        )}
        <ul className="mt-10 space-y-5">{list.map((q) => <InquiryCard key={q.id} q={q} onStatus={onStatus} />)}</ul>
        </>)}
      </Container>
    </section>
  );
}
